// Isolated error boundary for the Storyboard Studio layer.
// A crash here renders an inline error state — it cannot white-screen the Personal chat.

import { Component, createRef, type ReactNode, type ErrorInfo } from 'react';
import { Film } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class StoryboardErrorBoundary extends Component<Props, State> {
  state: State = { error: null };
  private fallbackRef = createRef<HTMLDivElement>();

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[storyboard-studio] layer crash:', error);
    console.error('component stack:', info.componentStack);
  }

  componentDidUpdate(_: Props, prevState: State): void {
    // Move focus to the error fallback when an error first appears,
    // so screen readers announce the error without user navigation.
    if (!prevState.error && this.state.error) {
      this.fallbackRef.current?.focus();
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          ref={this.fallbackRef}
          role="alert"
          tabIndex={-1}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            height: '100%',
            background: 'var(--bg-void)',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)',
            padding: '32px',
          }}
        >
          <Film size={32} strokeWidth={1.5} color="var(--error)" />
          <div style={{ color: 'var(--gold-500)', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>
            Storyboard Studio — Layer Error
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--error)', maxWidth: 400, textAlign: 'center' }}>
            {this.state.error.message}
          </div>
          <button
            style={{
              background: 'transparent',
              border: '1px solid var(--border-hover)',
              borderRadius: '6px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              padding: '8px 16px',
              textTransform: 'uppercase',
            }}
            onClick={() => this.setState({ error: null })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
